import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import {
  api,
  ApiError,
  AdminBankDepth,
  AdminCommentRow,
  AdminFormatDemandRow,
  AdminQuestionRow,
  AdminUsageRow,
} from "../lib/api";
import { useAuth } from "../lib/auth-context";
import {
  HBarRow,
  HistogramChart,
  HorizontalBarChart,
  ScatterChart,
  ScatterPoint,
} from "../lib/charts";

type Tab = "overview" | "usage" | "bank" | "demand" | "comments";

const TABS: { id: Tab; label: string; sub: string }[] = [
  { id: "overview", label: "Overview", sub: "Charts at a glance" },
  { id: "usage", label: "Students", sub: "Per-BlazerID usage" },
  { id: "bank", label: "Bank quality", sub: "Per-question signals" },
  { id: "demand", label: "Format demand", sub: "What students filter to" },
  { id: "comments", label: "Comments", sub: "Admin-only feedback" },
];

export default function Admin() {
  const { me } = useAuth();
  // URL-restore tab + include_demo so the "back to admin" link from the
  // drilldown returns the user to the same view.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = (searchParams.get("tab") as Tab | null) ?? "usage";
  const initialTab: Tab = TABS.some((t) => t.id === tabParam) ? tabParam : "usage";
  const [tab, setTab] = useState<Tab>(initialTab);
  const [includeDemo, setIncludeDemo] = useState(searchParams.get("include_demo") === "1");

  // Reflect state changes in the URL so reloads + back-from-detail preserve them.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", tab);
    if (includeDemo) next.set("include_demo", "1");
    else next.delete("include_demo");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, includeDemo]);

  if (!me) return null;
  if (!me.signed_in) return <Navigate to="/login" replace />;
  if (!me.is_admin) return <Navigate to="/" replace />;

  return (
    <div className="page page--wide">
      <div className="stack-lg">
        <header className="admin__header">
          <div>
            <h1 className="h1" style={{ marginBottom: 4 }}>Admin analytics</h1>
            <p className="lede" style={{ margin: 0 }}>
              Pilot usage + bank quality + format demand. All BlazerID-keyed.
            </p>
          </div>
          <div className="admin__actions">
            <label className="admin__demo-toggle">
              <input
                type="checkbox"
                checked={includeDemo}
                onChange={(e) => setIncludeDemo(e.target.checked)}
              />
              Include seed data
            </label>
            <a
              className="btn"
              href={api.adminExportCsvUrl(includeDemo)}
              download="usage.csv"
            >
              Export CSV
            </a>
          </div>
        </header>

        <BankDepthPanel />

        <nav className="admin__tabs" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              className={`admin__tab${tab === t.id ? " admin__tab--active" : ""}`}
              onClick={() => setTab(t.id)}
            >
              <span className="admin__tab__label">{t.label}</span>
              <span className="admin__tab__sub">{t.sub}</span>
            </button>
          ))}
        </nav>

        {tab === "overview" && <OverviewTab includeDemo={includeDemo} />}
        {tab === "usage" && <UsageTab includeDemo={includeDemo} />}
        {tab === "bank" && <BankTab includeDemo={includeDemo} />}
        {tab === "demand" && <DemandTab includeDemo={includeDemo} />}
        {tab === "comments" && <CommentsTab includeDemo={includeDemo} />}

        <DangerZone />
      </div>
    </div>
  );
}

// ---- Tab: overview (charts) ----

function OverviewTab({ includeDemo }: { includeDemo: boolean }) {
  const [usage, setUsage] = useState<AdminUsageRow[] | null>(null);
  const [demand, setDemand] = useState<AdminFormatDemandRow[] | null>(null);

  useEffect(() => {
    setUsage(null);
    setDemand(null);
    api.adminUsage(includeDemo).then((r) => setUsage(r.rows));
    api.adminFormatDemand(includeDemo).then((r) => setDemand(r.rows));
  }, [includeDemo]);

  if (!usage || !demand) return <Skeleton label="Loading charts…" />;

  // ---- Scatter: attempts vs. % correct (one dot per student with ≥1 attempt)
  const scatterPoints: ScatterPoint[] = usage
    .filter((r) => r.attempts > 0 && r.pct_correct !== null)
    .map((r) => ({ x: r.attempts, y: r.pct_correct!, label: r.blazerid }));

  // ---- Histogram: students by attempt count
  const BUCKETS: { label: string; lo: number; hi: number }[] = [
    { label: "0", lo: 0, hi: 0 },
    { label: "1–5", lo: 1, hi: 5 },
    { label: "6–15", lo: 6, hi: 15 },
    { label: "16–30", lo: 16, hi: 30 },
    { label: "31–60", lo: 31, hi: 60 },
    { label: "60+", lo: 61, hi: Infinity },
  ];
  const histo = BUCKETS.map((b) => ({
    label: b.label,
    count: usage.filter((r) => r.attempts >= b.lo && r.attempts <= b.hi).length,
  }));

  // ---- Format demand bars
  // Aggregate across topics: total selections + served per format value.
  const totals = new Map<string, { selections: number; served: number }>();
  for (const r of demand) {
    const t = totals.get(r.format) ?? { selections: 0, served: 0 };
    t.selections += r.selections;
    t.served += r.served;
    totals.set(r.format, t);
  }
  const formatLabel = (f: string) =>
    f === "all" ? "All formats"
    : f === "mc" ? "Multiple choice"
    : f === "fill_in" ? "Fill in the answer" : f;
  const FORMAT_ORDER = ["all", "mc", "fill_in"];
  const demandRows: HBarRow[] = FORMAT_ORDER
    .filter((f) => totals.has(f) || true) // always show all three; 0 reads as "no demand yet"
    .map((f) => {
      const t = totals.get(f) ?? { selections: 0, served: 0 };
      return {
        label: formatLabel(f),
        a: t.selections,
        b: t.served,
        aLabel: "picks",
        bLabel: "served",
      };
    });

  return (
    <div className="stack-lg">
      <ChartCard
        title="Usage vs. accuracy"
        subtitle="Each dot is a student. X = questions attempted, Y = % correct. The central pilot question: does practice volume correlate with performance?"
      >
        <ScatterChart
          points={scatterPoints}
          xLabel="Questions attempted"
          yLabel="% correct"
        />
      </ChartCard>

      <ChartCard
        title="Activity distribution"
        subtitle="How many students fall in each attempt-count band. Tall left = lots of dabblers; tall right = heavy users."
      >
        <HistogramChart buckets={histo} yLabel="Students" />
      </ChartCard>

      <ChartCard
        title="Format demand"
        subtitle="Where students point the format filter. Picks = explicit format_filter_changed events; served = questions delivered while that format was active. This is the signal that should drive where the next generation budget goes."
      >
        <HorizontalBarChart rows={demandRows} />
      </ChartCard>
    </div>
  );
}

function ChartCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <div className="chart-card">
      <div className="chart-card__head">
        <h2 className="chart-card__title">{title}</h2>
        <p className="chart-card__sub">{subtitle}</p>
      </div>
      <div className="chart-card__body">{children}</div>
    </div>
  );
}

// ---- Tab: usage ----

function UsageTab({ includeDemo }: { includeDemo: boolean }) {
  const [rows, setRows] = useState<AdminUsageRow[] | null>(null);
  useEffect(() => {
    setRows(null);
    api.adminUsage(includeDemo).then((r) => setRows(r.rows));
  }, [includeDemo]);

  if (!rows) return <Skeleton label="Loading students…" />;
  if (rows.length === 0) return <Empty label="No student activity yet." />;

  return (
    <div className="card stack" style={{ padding: 0 }}>
      <div className="admin__table-wrap">
        <table className="admin__table">
          <thead>
            <tr>
              <th>BlazerID</th>
              <th>Attempts</th>
              <th>Correct</th>
              <th>%</th>
              <th>Avg time</th>
              <th>Sessions</th>
              <th>Votes</th>
              <th>Comments</th>
              <th>Last active</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.blazerid}>
                <td>
                  <span className="admin__id">{r.blazerid}</span>
                  {r.is_admin && <span className="admin__badge">admin</span>}
                  {r.blazerid.startsWith("demo_") && (
                    <span className="admin__badge admin__badge--demo">seed</span>
                  )}
                </td>
                <td className="num">{r.attempts}</td>
                <td className="num">{r.correct}</td>
                <td className="num">{fmtPct(r.pct_correct)}</td>
                <td className="num">{fmtTime(r.avg_time_ms)}</td>
                <td className="num">{r.sessions}</td>
                <td className="num">{r.votes}</td>
                <td className="num">{r.comments}</td>
                <td className="num">{fmtRelative(r.last_active)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---- Tab: bank quality ----

function BankTab({ includeDemo }: { includeDemo: boolean }) {
  const [rows, setRows] = useState<AdminQuestionRow[] | null>(null);
  useEffect(() => {
    setRows(null);
    api.adminQuestions(includeDemo, 200).then((r) => setRows(r.rows));
  }, [includeDemo]);

  if (!rows) return <Skeleton label="Loading bank stats…" />;
  if (rows.length === 0) return <Empty label="No question activity yet." />;

  return (
    <div className="card stack" style={{ padding: 0 }}>
      <div className="admin__hint">
        Sorted by downvotes → skip rate → %-incorrect. Top rows are the
        worst-performing items: the ones engine review should look at.
      </div>
      <div className="admin__table-wrap">
        <table className="admin__table">
          <thead>
            <tr>
              <th>Q#</th>
              <th>Topic › Concept</th>
              <th>Format</th>
              <th>Diff.</th>
              <th>Served</th>
              <th>% correct</th>
              <th>Skip rate</th>
              <th>▲</th>
              <th>▼</th>
              <th>Avg time</th>
              <th>Cmts</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <BankRow key={r.question_id} r={r} includeDemo={includeDemo} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BankRow({ r, includeDemo }: { r: AdminQuestionRow; includeDemo: boolean }) {
  const href = `/admin/questions/${r.question_id}${includeDemo ? "?include_demo=1" : ""}`;
  return (
    <tr className="admin__row--clickable">
      <td className="num">
        <Link to={href} className="admin__row-link">q{r.question_id}</Link>
        {r.status && r.status !== "active" && (
          <span
            className="admin__badge admin__badge--demo"
            style={{ marginLeft: 6 }}
            title={`status: ${r.status}`}
          >
            {r.status}
          </span>
        )}
      </td>
      <td>
        <Link to={href} className="admin__row-link">
          <div className="admin__crumb">{r.topic_name ?? "-"}</div>
          <div>{r.concept_name}</div>
        </Link>
      </td>
      <td>{fmtRf(r.response_format)}</td>
      <td>{r.difficulty}</td>
      <td className="num">{r.served}</td>
      <td className="num">{fmtPct(r.pct_correct)}</td>
      <td className="num">{fmtPct(r.skip_rate)}</td>
      <td className="num">{r.upvotes || ""}</td>
      <td className="num" data-strong={r.downvotes > 0 || undefined}>
        {r.downvotes || ""}
      </td>
      <td className="num">{fmtTime(r.avg_time_ms)}</td>
      <td className="num">{r.n_comments || ""}</td>
    </tr>
  );
}

// ---- Tab: format demand ----

function DemandTab({ includeDemo }: { includeDemo: boolean }) {
  const [rows, setRows] = useState<AdminFormatDemandRow[] | null>(null);
  useEffect(() => {
    setRows(null);
    api.adminFormatDemand(includeDemo).then((r) => setRows(r.rows));
  }, [includeDemo]);

  if (!rows) return <Skeleton label="Loading demand signal…" />;
  if (rows.length === 0) return <Empty label="No format-filter activity yet." />;

  // Group by topic so each topic shows its three (or fewer) format buckets together.
  const byTopic = new Map<string, AdminFormatDemandRow[]>();
  for (const r of rows) {
    const key = r.topic_name ?? "(All topics)";
    const arr = byTopic.get(key) ?? [];
    arr.push(r);
    byTopic.set(key, arr);
  }

  return (
    <div className="card stack" style={{ padding: 0 }}>
      <div className="admin__hint">
        Where students point the format filter: selections AND implicit
        "filter active when served" from the served event. Highest-demand
        topic × format cells are where to point next generation spend.
      </div>
      <div className="admin__table-wrap">
        <table className="admin__table">
          <thead>
            <tr>
              <th>Topic</th>
              <th>Format</th>
              <th>Distinct students</th>
              <th>Picks</th>
              <th>Served under filter</th>
            </tr>
          </thead>
          <tbody>
            {[...byTopic.entries()].map(([topic, group]) =>
              group.map((r, i) => (
                <tr key={`${topic}-${r.format}`}>
                  {i === 0 ? (
                    <td rowSpan={group.length} className="admin__rowspan">{topic}</td>
                  ) : null}
                  <td>{fmtFmt(r.format)}</td>
                  <td className="num">{r.students}</td>
                  <td className="num">{r.selections}</td>
                  <td className="num">{r.served}</td>
                </tr>
              )),
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---- Tab: comments ----

function CommentsTab({ includeDemo }: { includeDemo: boolean }) {
  const [rows, setRows] = useState<AdminCommentRow[] | null>(null);
  useEffect(() => {
    setRows(null);
    api.adminComments(includeDemo, 200).then((r) => setRows(r.rows));
  }, [includeDemo]);

  if (!rows) return <Skeleton label="Loading comments…" />;
  if (rows.length === 0) return <Empty label="No comments yet." />;

  return (
    <div className="card stack" style={{ padding: 0 }}>
      <div className="admin__hint">
        Comments are admin-only. Frustrated comments are high-signal quality data.
      </div>
      <ul className="admin__comments">
        {rows.map((c) => (
          <li key={c.id} className="admin__comment">
            <div className="admin__comment__head">
              <span className="admin__id">{c.blazerid ?? "(unknown)"}</span>
              <span className="admin__crumb">
                q{c.question_id} · {c.concept_name}
              </span>
              <span className="admin__crumb">{fmtRelative(c.created_at)}</span>
            </div>
            <div className="admin__comment__body">{c.body}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---- Danger zone (pilot-data reset + backup download) ----

function DangerZone() {
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<
    | null
    | {
        deleted: Record<string, number>;
        preserved: Record<string, number>;
        backup_filename: string;
      }
  >(null);
  const [error, setError] = useState<string | null>(null);

  async function doReset() {
    setSubmitting(true);
    setError(null);
    try {
      const r = await api.adminResetPilotData();
      setResult(r);
      setConfirmOpen(false);
      setConfirmText("");
    } catch (err) {
      if (err instanceof ApiError) {
        setError(`Reset failed (${err.status}). Nothing was deleted.`);
      } else {
        setError("Reset failed. Nothing was deleted.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section
      className="card stack"
      style={{ borderColor: "var(--negative, #c33)", borderStyle: "solid" }}
    >
      <h2 className="qd__h2" style={{ margin: 0, color: "var(--negative, #c33)" }}>
        Danger zone
      </h2>
      <p className="muted" style={{ margin: 0, fontSize: 14 }}>
        Pilot operations. Both actions touch every user's data — read carefully.
      </p>

      <div className="stack" style={{ gap: 8 }}>
        <div>
          <strong>Download backup</strong>
          <p className="muted" style={{ margin: "2px 0 8px", fontSize: 13 }}>
            A consistent single-file copy of the live SQLite DB (via
            <code> VACUUM INTO </code>). Safe to run anytime.
          </p>
          <a className="btn" href={api.adminBackupDbUrl()} download>
            Download backup
          </a>
        </div>
      </div>

      <hr style={{ border: 0, borderTop: "1px solid var(--border, #ddd)", margin: "4px 0" }} />

      <div className="stack" style={{ gap: 8 }}>
        <div>
          <strong>Reset pilot data</strong>
          <p className="muted" style={{ margin: "2px 0 8px", fontSize: 13 }}>
            DESTRUCTIVE. Deletes every user, session, attempt, vote, comment, event,
            and rate-limit row. Preserves the question bank, coverage map, canon
            history, and access-code roster. Writes a same-volume backup BEFORE
            deleting. Your session will be wiped along with everyone else's — you'll
            be redirected to sign in again.
          </p>
          {!confirmOpen && !result && (
            <button
              type="button"
              className="btn"
              style={{
                background: "var(--negative, #c33)",
                color: "#fff",
                borderColor: "var(--negative, #c33)",
              }}
              onClick={() => setConfirmOpen(true)}
            >
              Reset pilot data…
            </button>
          )}
        </div>

        {confirmOpen && !result && (
          <div
            className="stack"
            style={{
              gap: 8,
              padding: 12,
              border: "1px solid var(--negative, #c33)",
              borderRadius: 6,
              background: "rgba(204, 51, 51, 0.06)",
            }}
          >
            <p style={{ margin: 0, fontSize: 14 }}>
              Type <code>RESET</code> to confirm.
            </p>
            <input
              className="input"
              type="text"
              autoFocus
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              spellCheck={false}
              autoCapitalize="off"
              style={{ fontFamily: "var(--font-mono)" }}
              disabled={submitting}
            />
            {error && (
              <div className="muted" style={{ color: "var(--negative, #c33)" }}>
                {error}
              </div>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                className="btn"
                style={{
                  background: "var(--negative, #c33)",
                  color: "#fff",
                  borderColor: "var(--negative, #c33)",
                }}
                disabled={submitting || confirmText !== "RESET"}
                onClick={doReset}
              >
                {submitting ? "Deleting…" : "Confirm reset"}
              </button>
              <button
                type="button"
                className="btn"
                disabled={submitting}
                onClick={() => {
                  setConfirmOpen(false);
                  setConfirmText("");
                  setError(null);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {result && (
          <div
            className="stack"
            style={{
              gap: 8,
              padding: 12,
              border: "1px solid var(--border, #ddd)",
              borderRadius: 6,
            }}
          >
            <p style={{ margin: 0 }}>
              <strong>Reset complete.</strong> Backup saved as{" "}
              <code>{result.backup_filename}</code>.
            </p>
            <ResetCounts deleted={result.deleted} preserved={result.preserved} />
            <button
              type="button"
              className="btn"
              onClick={() =>
                navigate("/access-code?reset=1", { replace: true })
              }
            >
              Go to sign-in
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

function ResetCounts({
  deleted,
  preserved,
}: {
  deleted: Record<string, number>;
  preserved: Record<string, number>;
}) {
  return (
    <div style={{ fontSize: 13 }}>
      <div className="muted" style={{ marginBottom: 4 }}>
        Per-table counts:
      </div>
      <table className="admin__table" style={{ fontSize: 13 }}>
        <thead>
          <tr>
            <th>Table</th>
            <th>State</th>
            <th className="num">Rows</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(deleted).map(([t, n]) => (
            <tr key={`d-${t}`}>
              <td>{t}</td>
              <td>deleted</td>
              <td className="num">{n}</td>
            </tr>
          ))}
          {Object.entries(preserved).map(([t, n]) => (
            <tr key={`p-${t}`}>
              <td>{t}</td>
              <td>preserved</td>
              <td className="num">{n}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---- Bank depth (bank vs generated) ----

function BankDepthPanel() {
  const [data, setData] = useState<AdminBankDepth | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.adminBankDepth().then(setData).catch(() => setError(true));
  }, []);

  if (error) return null;
  if (!data) {
    return (
      <section className="card">
        <p className="muted" style={{ margin: 0 }}>Loading bank depth…</p>
      </section>
    );
  }

  const q = data.questions;
  const a = data.answers_by_source;
  const g = data.generations;
  const answersTotal = a.from_bank + a.generated;
  const fromBankShare =
    answersTotal > 0 ? Math.round((a.from_bank / answersTotal) * 100) : null;

  const yieldOf = (att: number, suc: number) =>
    att > 0 ? `${Math.round((suc / att) * 100)}% yield` : "no attempts yet";

  return (
    <section className="card stack" style={{ gap: 12 }}>
      <div>
        <h2 className="qd__h2" style={{ margin: 0 }}>Bank depth</h2>
        <p className="muted" style={{ margin: "2px 0 0", fontSize: 13 }}>
          Did the pre-seeded bank hold up, or did we end up generating live?
          Attempts = provider spend; successes = verified rows landed in the bank.
        </p>
      </div>

      <div className="stat-grid">
        <Stat label="Active questions (total)" value={String(q.total)} />
        <Stat
          label="From bank (imported)"
          value={String(q.from_bank)}
          sub={pctOf(q.from_bank, q.total)}
        />
        <Stat
          label="Generated live"
          value={String(q.generated)}
          sub={pctOf(q.generated, q.total)}
        />
        <Stat
          label="Answers served — from bank"
          value={String(a.from_bank)}
          sub={fromBankShare !== null ? `${fromBankShare}% of all answers` : "no answers yet"}
        />
        <Stat
          label="Answers served — generated"
          value={String(a.generated)}
          sub={
            answersTotal > 0
              ? `${100 - (fromBankShare ?? 0)}% of all answers`
              : "no answers yet"
          }
        />
        <Stat
          label="Gen attempts — last 24h"
          value={`${g.attempts.last_24h} / ${g.budget}`}
          sub={`daily budget · ${g.successes.last_24h} succeeded`}
        />
        <Stat
          label="Gen attempts — last 7d"
          value={String(g.attempts.last_7d)}
          sub={`${g.successes.last_7d} succeeded · ${yieldOf(g.attempts.last_7d, g.successes.last_7d)}`}
        />
        <Stat
          label="Gen attempts — total"
          value={String(g.attempts.total)}
          sub={`${g.successes.total} succeeded · ${yieldOf(g.attempts.total, g.successes.total)}`}
        />
        <Stat
          label="Budget-exhausted events"
          value={String(g.budget_exhausted_events)}
          sub={g.budget_exhausted_events > 0 ? "budget has tripped at least once" : "never tripped"}
        />
      </div>
    </section>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="stat-card">
      <div className="stat-card__label">{label}</div>
      <div className="stat-card__value">{value}</div>
      {sub && <div className="stat-card__label" style={{ marginTop: 2, opacity: 0.7 }}>{sub}</div>}
    </div>
  );
}

function pctOf(n: number, total: number): string | undefined {
  if (!total) return undefined;
  const p = Math.round((n / total) * 100);
  return `${p}% of bank`;
}

// ---- Bits ----

function Skeleton({ label }: { label: string }) {
  return (
    <div className="card">
      <p className="muted" style={{ margin: 0 }}>{label}</p>
    </div>
  );
}

function Empty({ label }: { label: string }) {
  return (
    <div className="card" style={{ borderStyle: "dashed" }}>
      <p className="muted" style={{ margin: 0 }}>{label}</p>
    </div>
  );
}

function fmtPct(v: number | null): string {
  if (v === null || v === undefined) return "-";
  return `${Math.round(v * 100)}%`;
}

function fmtTime(ms: number | null): string {
  if (ms === null || ms === undefined) return "-";
  if (ms < 1000) return `${Math.round(ms)} ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
}

function fmtRelative(iso: string | null): string {
  if (!iso) return "-";
  const t = new Date(iso.replace(" ", "T") + "Z").getTime();
  if (Number.isNaN(t)) return iso;
  const dMs = Date.now() - t;
  const min = Math.round(dMs / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.round(hr / 24)}d ago`;
}

function fmtFmt(f: string): string {
  if (f === "all") return "All formats";
  if (f === "mc") return "Multiple choice";
  if (f === "fill_in") return "Fill in the answer";
  return f;
}

function fmtRf(rf: string): string {
  if (rf === "multiple_choice") return "MC";
  if (rf === "single_numeric") return "Numeric";
  if (rf === "multi_part") return "Multi-part";
  if (rf === "free_text") return "Free text";
  return rf;
}
